import React, { useState, useEffect, useMemo } from 'react';
import { useTheme } from '@mui/material/styles';
import { Search, Calendar, AlertTriangle, Loader2 } from 'lucide-react';
import ReceiptDetail from './ReceiptDetail';
import receiptsApi from './services/receiptsApi';

// ---------------------------------------------------------------------
// Periods list (page 1) — loads real periods + their cash/online totals
// from GET /api/receipt-periods/ (mpesa/views/period_receipts_views.py).
// "View Receipts" drills into ReceiptDetail.js (page 2).
//
// `type` (School Fees / Regular) has no backend source yet — the filter
// dropdown below is inert until a period gains a real `type` field; see
// the note on filteredPeriods.
// ---------------------------------------------------------------------

const getStatusBadgeColor = (status, isDark) => {
  switch (status) {
    case 'open': return isDark ? 'bg-blue-900/40 text-blue-300' : 'bg-blue-100 text-blue-800';
    case 'active': return isDark ? 'bg-green-900/40 text-green-300' : 'bg-green-100 text-green-800';
    case 'closed': return isDark ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-800';
    default: return isDark ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-800';
  }
};

const formatKSh = (amount) => `KSh ${Math.round(amount || 0).toLocaleString()}`;

// "5 Aug 2026" — same short date convention used across the app.
const formatShortDate = (dateString) => {
  if (!dateString) return 'N/A';
  return new Date(dateString).toLocaleDateString('en-GB', {
    day: 'numeric', month: 'short', year: 'numeric',
  });
};

// Default date range: past 45 days, filtered against meeting date.
const getPast45DaysRange = () => {
  const end = new Date();
  const start = new Date();
  start.setDate(start.getDate() - 45);
  return {
    startDate: start.toISOString().split('T')[0],
    endDate: end.toISOString().split('T')[0],
  };
};

// "August 2026 Meeting" — computed client-side from the real meetingDate,
// no backend field needed for this.
const monthLabel = (dateString) => {
  const d = new Date(dateString);
  const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
  return `${monthNames[d.getMonth()]} ${d.getFullYear()} Meeting`;
};

// Display order for the periods table — not chronological, priority-based:
//   0. Open, period end in the past (overdue — needs attention first)
//   1. Active
//   2. Open, period end is today
//   3. Closed, period end within the last 30 days
//   4. Open, period end in the future
//   5. Closed, everything else (older than 30 days, or any other case)
//   6. Fallback for anything with no meeting date / unrecognized status
const getPeriodRank = (p) => {
  if (!p.meetingDate) return 6;

  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const periodEnd = new Date(p.meetingDate);
  periodEnd.setHours(0, 0, 0, 0);
  const daysSincePeriodEnd = Math.round((today - periodEnd) / (1000 * 60 * 60 * 24));

  if (p.status === 'active') return 1;

  if (p.status === 'open') {
    if (daysSincePeriodEnd > 0) return 0;   // period end already passed
    if (daysSincePeriodEnd === 0) return 2; // period end is today
    return 4;                                // period end still ahead
  }

  if (p.status === 'closed') {
    return (daysSincePeriodEnd >= 0 && daysSincePeriodEnd <= 30) ? 3 : 5;
  }

  return 6;
};

const Receipts = () => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  const [searchQuery, setSearchQuery] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [periods, setPeriods] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dateRange, setDateRange] = useState(getPast45DaysRange());
  const [activeDatePreset, setActiveDatePreset] = useState('past45'); // 'past45' | 'month' | 'custom' | 'all'
  const [selectedMonth, setSelectedMonth] = useState('');
  const [monthMenuAnchor, setMonthMenuAnchor] = useState(null);
  const [customDateDialogOpen, setCustomDateDialogOpen] = useState(false);
  const [tempDateRange, setTempDateRange] = useState({ startDate: '', endDate: '' });

  // Which period's receipts are being viewed (page 2). null = periods list.
  const [selectedPeriodId, setSelectedPeriodId] = useState(null);

  useEffect(() => {
    loadPeriods();
    // eslint-disable-next-line
  }, [dateRange]);

  const loadPeriods = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await receiptsApi.fetchReceiptPeriods({
        startDate: dateRange.startDate,
        endDate: dateRange.endDate,
      });
      setPeriods(data.map((p) => ({ ...p, periodName: monthLabel(p.meetingDate) })));
    } catch (err) {
      console.error('Error loading periods:', err);
      setError('Failed to load periods');
    } finally {
      setLoading(false);
    }
  };

  const handleCustomDateApply = () => {
    setDateRange(tempDateRange);
    setSelectedMonth('');
    setActiveDatePreset('custom');
    setCustomDateDialogOpen(false);
  };

  const handleClearDateFilter = () => {
    setDateRange({ startDate: '', endDate: '' });
    setSelectedMonth('');
    setActiveDatePreset('all');
  };

  const handlePast45Days = () => {
    setDateRange(getPast45DaysRange());
    setSelectedMonth('');
    setActiveDatePreset('past45');
  };

  const filteredPeriods = useMemo(() => {
    let filtered = [...periods];

    if (searchQuery.trim() !== '') {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(p => p.group?.name?.toLowerCase().includes(query));
    }

    // No backend `type` (School Fees / Regular) field on a period yet —
    // only filter by it for periods that happen to carry one, so this
    // doesn't silently hide every row once wired to the real API.
    if (typeFilter !== 'all') {
      filtered = filtered.filter(p => !p.type || p.type === typeFilter);
    }

    if (dateRange.startDate || dateRange.endDate) {
      filtered = filtered.filter(p => {
        if (!p.meetingDate) return false;
        const date = new Date(p.meetingDate);
        const start = dateRange.startDate ? new Date(dateRange.startDate) : null;
        const end = dateRange.endDate ? new Date(dateRange.endDate) : null;
        if (start && end) return date >= start && date <= end;
        if (start) return date >= start;
        if (end) return date <= end;
        return true;
      });
    }

    // Priority order (see getPeriodRank above), then most recent period end
    // first within the same bucket.
    filtered.sort((a, b) => {
      const rankDiff = getPeriodRank(a) - getPeriodRank(b);
      if (rankDiff !== 0) return rankDiff;
      return new Date(b.meetingDate) - new Date(a.meetingDate);
    });

    return filtered;
  }, [periods, searchQuery, typeFilter, dateRange]);

  const selectedPeriod = periods.find(p => p.id === selectedPeriodId) || null;

  // -----------------------------------------------------------------
  // Page 2: receipt detail for one period — its own component.
  // -----------------------------------------------------------------
  if (selectedPeriodId) {
    return (
      <ReceiptDetail
        period={selectedPeriod}
        isDark={isDark}
        onBack={() => setSelectedPeriodId(null)}
      />
    );
  }

  // -----------------------------------------------------------------
  // Page 1: periods list
  // -----------------------------------------------------------------
  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
        <span className={`ml-3 ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>Loading periods...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className={`rounded-lg p-6 ${isDark ? 'bg-red-900/20 border border-red-800' : 'bg-red-50 border border-red-200'}`}>
        <p className={isDark ? 'text-red-400' : 'text-red-600'}>{error}</p>
        <button onClick={loadPeriods} className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="pb-32">
      <div className={`rounded-lg shadow-sm border overflow-hidden ${isDark ? 'bg-[#2b323c] border-gray-700' : 'bg-white border-gray-200'}`}>
        {/* Title */}
        <div className={`px-5 py-4 border-b ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
          <h2 className={`text-xl font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>Receipts</h2>
          <p className={`text-sm mt-0.5 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
            Not yet connected to the receipt printer.
          </p>
        </div>

        {/* Filters */}
        <div className={`flex flex-wrap items-center gap-3 px-5 py-4 border-b ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
          <div className="relative flex-1 min-w-[220px]">
            <Search className={`absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 ${isDark ? 'text-gray-500' : 'text-gray-400'}`} />
            <input
              type="text"
              placeholder="Search by group name..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className={`w-full pl-10 pr-4 py-2 border rounded-lg transition-colors ${
                isDark
                  ? 'bg-[#2b323c] border-gray-700 text-gray-200 placeholder-gray-500 focus:border-blue-500'
                  : 'bg-white border-gray-300 text-gray-900 placeholder-gray-400 focus:border-blue-500'
              } focus:outline-none focus:ring-2 focus:ring-blue-500`}
            />
          </div>

          <select
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
            className={`px-4 py-2 border rounded-lg transition-colors ${
              isDark
                ? 'bg-[#2b323c] border-gray-700 text-gray-200 focus:border-blue-500'
                : 'bg-white border-gray-300 text-gray-900 focus:border-blue-500'
            } focus:outline-none focus:ring-2 focus:ring-blue-500`}
          >
            <option value="all">All Types</option>
            <option value="sf">School Fees (SF)</option>
            <option value="regular">Regular</option>
          </select>

          <button
            onClick={handlePast45Days}
            className={`px-4 py-2 border rounded-lg transition-colors flex items-center justify-center gap-2 ${
              activeDatePreset === 'past45'
                ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-400'
                : isDark
                  ? 'bg-[#2b323c] border-gray-700 text-gray-200 hover:border-blue-500'
                  : 'bg-white border-gray-300 text-gray-900 hover:border-blue-500'
            } focus:outline-none focus:ring-2 focus:ring-blue-500`}
          >
            <Calendar className="w-4 h-4" />
            Past 45 Days
          </button>

          <button
            onClick={(e) => setMonthMenuAnchor(e.currentTarget)}
            className={`px-4 py-2 border rounded-lg transition-colors text-left flex items-center justify-between ${
              activeDatePreset === 'month'
                ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-400'
                : isDark
                  ? 'bg-[#2b323c] border-gray-700 text-gray-200 hover:border-blue-500'
                  : 'bg-white border-gray-300 text-gray-900 hover:border-blue-500'
            } focus:outline-none focus:ring-2 focus:ring-blue-500`}
          >
            <span>
              {selectedMonth !== '' ? (() => {
                const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
                const [year, monthIndex] = selectedMonth.split('-');
                return `${monthNames[parseInt(monthIndex)]} ${year}`;
              })() : 'Select Month (Meeting Date)'}
            </span>
            <Calendar className="w-4 h-4 ml-2" />
          </button>

          <button
            onClick={() => {
              setTempDateRange(dateRange);
              setCustomDateDialogOpen(true);
            }}
            className={`px-4 py-2 border rounded-lg transition-colors flex items-center justify-center gap-2 ${
              activeDatePreset === 'custom'
                ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-400'
                : isDark
                  ? 'bg-[#2b323c] border-gray-700 text-gray-200 hover:border-blue-500'
                  : 'bg-white border-gray-300 text-gray-900 hover:border-blue-500'
            } focus:outline-none focus:ring-2 focus:ring-blue-500`}
          >
            <Calendar className="w-4 h-4" />
            Custom
          </button>
        </div>

        {/* Table */}
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className={`border-b ${isDark ? 'bg-gray-800 border-gray-700' : 'bg-gray-50 border-gray-200'}`}>
              <tr>
                <th className={`px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>Meeting Date</th>
                <th className={`px-6 py-3 text-center text-xs font-semibold uppercase tracking-wider ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>Status</th>
                <th className={`px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>Group Name</th>
                <th className={`px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>Cash Remitted</th>
                <th className={`px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>Online Payments</th>
                <th className={`px-6 py-3 text-center text-xs font-semibold uppercase tracking-wider ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>Action</th>
              </tr>
            </thead>
            <tbody className={`divide-y ${isDark ? 'divide-gray-700' : 'divide-gray-200'}`}>
              {filteredPeriods.map((p) => (
                <tr key={p.id} className={isDark ? 'hover:bg-gray-700' : 'hover:bg-gray-50'}>
                  <td className={`px-6 py-4 font-medium ${isDark ? 'text-gray-200' : 'text-gray-900'}`}>
                    {formatShortDate(p.meetingDate)}
                  </td>
                  <td className="px-6 py-4 text-center">
                    <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold uppercase ${getStatusBadgeColor(p.status, isDark)}`}>
                      {p.status}
                    </span>
                  </td>
                  <td className={`px-6 py-4 font-medium ${isDark ? 'text-gray-200' : 'text-gray-900'}`}>
                    {p.group?.name || 'N/A'}
                  </td>
                  <td className={`px-6 py-4 ${isDark ? 'text-gray-300' : 'text-gray-900'}`}>
                    {formatKSh(p.cashRemitted)}
                  </td>
                  <td className={`px-6 py-4 ${isDark ? 'text-gray-300' : 'text-gray-900'}`}>
                    {formatKSh(p.onlinePayments)}
                  </td>
                  <td className="px-6 py-4 text-center">
                    <button
                      onClick={() => setSelectedPeriodId(p.id)}
                      className="px-4 py-1.5 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition-colors"
                    >
                      View Receipts
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {filteredPeriods.length === 0 && (
          <div className={`text-center py-12 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
            <AlertTriangle className={`w-12 h-12 mx-auto mb-4 ${isDark ? 'text-gray-500' : 'text-gray-400'}`} />
            <p className="text-lg">No periods found matching your filters.</p>
          </div>
        )}

        <div className={`px-5 py-3 border-t text-sm ${isDark ? 'border-gray-700 text-gray-400' : 'border-gray-200 text-gray-600'}`}>
          Showing {filteredPeriods.length} of {periods.length} periods
        </div>
      </div>

      {/* Month Selection Menu */}
      {monthMenuAnchor && (
        <div className="fixed inset-0 z-[1400] flex items-start justify-center pt-20">
          <div className="absolute inset-0 bg-black/20" onClick={() => setMonthMenuAnchor(null)}></div>
          <div className={`relative z-10 rounded-lg shadow-xl border min-w-[200px] max-h-[500px] overflow-y-auto ${
            isDark ? 'bg-[#2b323c] border-gray-700' : 'bg-white border-gray-200'
          }`}>
            {(() => {
              const currentDate = new Date();
              const currentYear = currentDate.getFullYear();
              const currentMonth = currentDate.getMonth();
              const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

              const months = [];
              for (let i = 0; i < 12; i++) {
                const date = new Date(currentYear, currentMonth - i, 1);
                const monthIndex = date.getMonth();
                const year = date.getFullYear();
                months.push({
                  label: `${monthNames[monthIndex]} ${year}`,
                  monthIndex: monthIndex,
                  year: year,
                  value: `${year}-${monthIndex}`,
                  isCurrent: i === 0
                });
              }

              return months.map((month) => (
                <button
                  key={month.value}
                  onClick={() => {
                    const startDate = new Date(month.year, month.monthIndex, 1);
                    const endDate = new Date(month.year, month.monthIndex + 1, 0);
                    setDateRange({
                      startDate: startDate.toISOString().split('T')[0],
                      endDate: endDate.toISOString().split('T')[0]
                    });
                    setSelectedMonth(month.value);
                    setActiveDatePreset('month');
                    setMonthMenuAnchor(null);
                  }}
                  className={`w-full px-4 py-2 text-left transition-colors flex items-center justify-between ${
                    selectedMonth === month.value
                      ? isDark ? 'bg-blue-900/40 text-blue-300' : 'bg-blue-50 text-blue-700'
                      : isDark ? 'hover:bg-gray-700 text-gray-200' : 'hover:bg-gray-50 text-gray-900'
                  }`}
                >
                  <span>{month.label}</span>
                  {month.isCurrent && (
                    <span className={`text-xs px-2 py-0.5 rounded ${
                      isDark ? 'bg-green-900/40 text-green-300' : 'bg-green-100 text-green-700'
                    }`}>
                      Current
                    </span>
                  )}
                </button>
              ));
            })()}
            <div className={`border-t ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
              <button
                onClick={handleClearDateFilter}
                className={`w-full px-4 py-2 text-left transition-colors ${
                  isDark ? 'hover:bg-gray-700 text-red-400' : 'hover:bg-gray-50 text-red-600'
                }`}
              >
                Clear Filter
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Custom Date Range Dialog */}
      {customDateDialogOpen && (
        <div className="fixed inset-0 z-[1400] flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/50" onClick={() => setCustomDateDialogOpen(false)}></div>
          <div className={`relative z-10 rounded-lg shadow-xl max-w-md w-full ${
            isDark ? 'bg-[#2b323c] border border-gray-700' : 'bg-white'
          }`}>
            <div className={`px-6 py-4 border-b ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
              <h3 className={`text-lg font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>Custom Date Range</h3>
            </div>
            <div className="px-6 py-4 space-y-4">
              <div>
                <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>Start Date</label>
                <input
                  type="date"
                  value={tempDateRange.startDate}
                  onChange={(e) => setTempDateRange({ ...tempDateRange, startDate: e.target.value })}
                  className={`w-full px-3 py-2 border rounded-lg ${
                    isDark ? 'bg-[#1e252d] border-gray-700 text-gray-200' : 'bg-white border-gray-300 text-gray-900'
                  }`}
                  style={{ colorScheme: isDark ? 'dark' : 'light' }}
                />
              </div>
              <div>
                <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>End Date</label>
                <input
                  type="date"
                  value={tempDateRange.endDate}
                  onChange={(e) => setTempDateRange({ ...tempDateRange, endDate: e.target.value })}
                  className={`w-full px-3 py-2 border rounded-lg ${
                    isDark ? 'bg-[#1e252d] border-gray-700 text-gray-200' : 'bg-white border-gray-300 text-gray-900'
                  }`}
                  style={{ colorScheme: isDark ? 'dark' : 'light' }}
                />
              </div>
            </div>
            <div className={`px-6 py-4 border-t flex justify-end gap-2 ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
              <button
                onClick={() => setCustomDateDialogOpen(false)}
                className={`px-4 py-2 rounded-lg transition-colors ${
                  isDark ? 'bg-gray-700 text-gray-200 hover:bg-gray-600' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                Cancel
              </button>
              <button
                onClick={handleClearDateFilter}
                className="px-4 py-2 rounded-lg bg-red-600 text-white hover:bg-red-700 transition-colors"
              >
                Clear
              </button>
              <button
                onClick={handleCustomDateApply}
                className="px-4 py-2 rounded-lg bg-blue-600 text-white hover:bg-blue-700 transition-colors"
              >
                Apply
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Receipts;